import { HttpCache } from '@/modules/cache/http'
import { UUID } from '@/modules/util/uuid'

export class HTTP {
	/**
	 * @type {HttpCache}
	 * @description Cache for HTTP requests
	 */
	static cache = new HttpCache

	/**
     *  @type {Object}
     *  @description Cached requests.
     */
    static cached = {}

    /**
	 * @type {Object}
	 * @description Request and response interceptors
	 */
    static interceptors = {
    	request: null,
    	response: null,
    }

    /**
	 * @type {String}
	 * @description Base URL for HTTP requests
	 */
    base

	/**
	 * @type {Object}
	 * @description Default settings for HTTP requests
	 */
	settings = {
		headers: {
			'Content-Type': 'application/json',
		},
		timeout: 1000 * 60,
	}

	/**
	 * @type {Object}
	 * @description Pending requests
	 */
	pending = {}

	/**
	 * @type {Boolean}
	 * @description Cancel flag. 
	 * 
	 * Should be set true by chaining the HTTP.cancel() method before calling HTTP.do() method (or any method which invokes .do(), such as child class API fetchers).
	 * Pending requests will then be aborted based on the specificity of data passed to the .do() method.
	 */
	_cancel = false

    _retryTimeout = null
    
    constructor(base, settings={}) {
    	this.base = base
    	this.settings = {
    		...this.settings,
    		...settings,
    	}
    }
    
    /**
	 * @description Perform an HTTP request with the specified path, method, params, body and class settings.
	 * 
	 * @param {String} path The path to make the request to. Will be appended to the instance base URL.
	 * @param {String} method The HTTP method to use for the request.
	 * @param {Object} params Query parameters for the request.
	 * @param {Object} body Body data for the request.
	 * @param {Boolean} cache Whether to cache the request (and retry later) if it fails.
     * 
	 * @returns {Object}
	 */
    async do(path, method, params, body, cache=false) {	
    	let config = {
    		method,
    		headers: this.settings.headers,
    		params,
    		body,
    	}

    	if (this._cancel) {
    		this._abort(path, config)
    		this._cancel = false
    		return
    	}

    	if (HTTP.interceptors.request) {
    		config = await HTTP.interceptors.request(this, config)
    		if (!config) {
    			return
    		}
    	}

    	if (cache) {
    		const uid = await HTTP.cache.set({
    			path,
    			config,
    			url: HTTP._formatUrl(this.base + path, config.params),
    			request: {
    				method: config.method,
    				headers: config.headers,
    				body: JSON.stringify(config.body),
    			},
    		})

    		HTTP.initCacheRetry()

    		return {
    			uid,
    		}
    	}
		
    	const httpRes = await this._fetch(path, config, this.settings.timeout, cache)
    	if  (!httpRes.ok) {
    		return httpRes
    	}

    	const res = {
    		status: httpRes.status,
    		url: httpRes.url,
    		body: await HTTP._streamToJSON(httpRes.body)
    	}
        
    	if (HTTP.interceptors.response) {
    		return await HTTP.interceptors.response(this, res)
    	}
        
    	return res
    }	
    
    /**
	 * @description Perform a GET request with the specified path, data and cache settings.
	 * Data is passed in the URL as query parameters.
	 * 
	 * @param {String} path 
	 * @param {Object} data 
	 * @param {Boolean} cache 
	 * @returns 
	 */
    async get(path, data={}, cache=false) {
    	return await this.do(path, 'GET', data, undefined, cache)
    }
    
    /**
	 * @description Perform a GET request with the specified path, data and cache settings.
	 * Data is set as a JSON string in the request body.
	 * 
	 * @param {String} path 
	 * @param {Object} data 
	 * @param {Boolean} cache 
	 * @returns 
	 */
    async post(path, data={}, cache=false) {
    	return await this.do(path, 'POST', undefined, data, cache)
    }

    /**
	 * @description Wait for all pending requests to complete.
	 * 
	 * Returns an array of the cache UIDs for any requests which failed.
	 */
    async wait() {
    	return await Promise.all(Object.values(this.pending).map(p => p.promise))
    }

    /**
	 * @description Prepare a HTTP method or child method which calls HTTP.do() to abort requests matching the specificity of the data passed to the following method.
	 * 
	 * For example, if we have a child class `ApiService extends HTTP`, 
	 * and we have a method `apiService.getItem('/path', { id: 10 })` which makes a call to `HTTP.do()`, `.get()` or `.post()`, 
	 * we can cancel all pending requests to '/path' with request data of `{ id: 10 }` by chaining the `cancel()` method before calling the method:
	 * 
	 * `apiService.cancel().get('/path', { id: 10 })`
	 * 
	 * @example
	 * // Cancel all pending requests
	 * http.cancel().do() OR http.abort()
	 * 
	 * // Cancel all pending requests for a specific path
	 * http.cancel().post('/path') OR http.abort('/path', 'POST')
	 * 
	 * // Cancel all pending requests for a specific path and request data
	 * http.cancel().get('/path', { id: 10 }) OR http.abort('/path', 'GET', { id: 10 })
	 * 
	 * @returns {HTTP}
	 */
    cancel() {
    	this._cancel = true
    	return this
    }

    /**
	 * @description Abort all pending requests.
	 * 
	 * @param {String|undefined} path 
	 * @param {String|undefined} method 
	 * @param {Object|undefined} data 
	 * 
	 * @example
	 * // Cancel all pending requests
	 * http.abort()
	 * 
	 * // Cancel all pending requests for a specific path
	 * http.abort('/path', 'POST')
	 * 
	 * // Cancel all pending requests for a specific path and request data
	 * http.abort('/path', 'GET', { id: 10 })
	 */
    abort(path=undefined, method=undefined, data=undefined, abort='UserAbort') {
    	this._abort(path, {
    		method,
    		params: method === 'GET' ? data : undefined,
    		body: method === 'POST' ? data : undefined,
    	}, abort)
    }

    _abort(path=undefined, config={}, abort='UserAbort') {
    	const shallowObjectCompare = (a, b) => {
    		return Object.keys(a).every(key => {
    			return (a[key] === undefined) || a[key] === b[key]
    		})
    	}

    	Object.values(this.pending).forEach(pending => {
    		if ((path && path !== pending.path)
				|| (config.method && config.method !== pending.config.method)
				|| (config.params && !shallowObjectCompare(config.params, pending.config.params))
				|| (config.body && !shallowObjectCompare(config.body, pending.config.body))
    		) {
    			return
    		}

    		pending.controller.abort(abort)
    	})
    }

    async _fetch(path, config, ms) {
    	const controller = new AbortController()
    	const timeout = setTimeout(() => controller.abort('TimeoutAbort'), ms)

    	const url = HTTP._formatUrl(this.base + path, config.params)
    	const request = {
    		method: config.method,
    		headers: config.headers,
    		body: JSON.stringify(config.body),
    		signal: controller.signal,
    	}

    	let resolve
    	const promise = new Promise(res => resolve = res)
    	const id = new UUID().get()

    	const pending = {
    		controller,
    		path,
    		config,
    		promise,
    		resolve,
    		id,
    	}

    	this.pending[id] = pending

    	try {
    		return await fetch(url, request)
    	} finally {
    		clearTimeout(timeout)
    		delete this.pending[id]
    		resolve()
    	}
    }

    static async _streamToJSON(stream) {
    	const reader = stream.getReader()
    	const decoder = new TextDecoder()
    	let result = ''

    	try {
    		let finished = false
    		while (!finished) {
    			const { done, value } = await reader.read()

    			if (done) {
    				finished = true
    			}

    			result += decoder.decode(value, { stream: true })
    		}

    		// Parse the accumulated result as JSON
    		return JSON.parse(result)
    	} finally {
    		reader.releaseLock()
    	}
    }

    static _formatUrl(url, params = {}) {
    	let formatted = new URL(url)

    	for (const [key, value] of Object.entries(params)) {
    		formatted.searchParams.append(key, value)
    	}

    	return formatted.toString()
    }

    static async retryCached(uid, intercept=false) {
    	let item = await HTTP.cache.get(uid)
    	if (item?.value?.response) {
    		return item.value.response
    	}

    	if (item && intercept && HTTP.interceptors.cacheRequest) {
    		item = await HTTP.interceptors.cacheRequest(item)
    	}

    	if (!item) {
    		return null
    	}

	    const { url, request } = item.value

	    try {
		    const r = await fetch(url, request)

		    if (!r.ok) {
    			console.error(`cached request ${uid}: ${r.status} - ${JSON.stringify(await HTTP._streamToJSON(r.body)) || ''}`)
    			await HTTP.cache.remove(uid)
    			return r
    		}

    	    let res = {
    		    status: r.status,
    		    url: r.url,
    		    body: await HTTP._streamToJSON(r.body)
    	    }
        
    	    if (intercept && HTTP.interceptors.cacheResponse) {
    		    await HTTP.interceptors.cacheResponse(this, res, item)
    	    }

    		return res
    	} catch (e) {
    		return { ok: false }
    	}
    }

    static async deleteCached(uid) {
    	try {
    		await HTTP.cache.remove(uid)
    		return true
    	} catch (e) {
    		console.error('Error deleting cached request:', e.message)
    		return false
    	}
    }

    static async initCacheRetry() {
    	if  (HTTP._retryTimeout) {
    		return
    	}

    	HTTP._retryTimeout = setTimeout(HTTP._cacheRetry, 1000)
    }

    static async stopCacheRetry() {
    	if (HTTP._retryTimeout) {
    		clearTimeout(HTTP._retryTimeout)
    		HTTP._retryTimeout = null
    	}
    }

    static async _cacheRetry() {
    	const keys = await HTTP.cache.keys()
    	if (!keys || keys.length === 0) {
    		clearTimeout(HTTP._retryTimeout)
    		HTTP._retryTimeout = null
    		return
    	}

    	for (const key of keys) {
    		if (!HTTP._retryTimeout) {
    			return
    		}
            
    		await HTTP.retryCached(key, true)
    	}

    	HTTP._retryTimeout = setTimeout(HTTP._cacheRetry, 3000)
    }
}
